import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDebug } from '../contexts/DebugContext';
import api from '../services/api';
import JplModal from './JplModal';
import { TaskDataHeaders, TaskDataCells, TimeDetailBlock, taskDataColumnCount, th, thR, td, tdR, linkBtn } from './TaskLines';
import { displayDate } from '../utils/dates';
import { money } from '../utils/format';
import { approvalPill, ApprovalHistoryTable } from './ApprovalStatus';
import { PageHeader, Banner, IconButton, WideSection, SortArrows, FilterButtons, JobPageLink } from './ui';
import InvoiceExtras from './InvoiceExtras';

const invNo = (n) => `#${n}`;

// Parse the job-list InvoiceDocs ("<DocNo>:<isPosted>;…") into objects.
function parseInvoiceDocs(s) {
  return (s || '').split(';').map((x) => x.trim()).filter(Boolean).map((x) => {
    const i = x.lastIndexOf(':');
    return { docNo: i >= 0 ? x.slice(0, i) : x, isPosted: i >= 0 && x.slice(i + 1) === '1' };
  });
}

// Color precedence (user-chosen): Unreviewed > Rejected > Green > Red.
//   none   : a draft invoice has no decision yet (unreviewed), or a posted
//            invoice exists with no approval recorded.
//   orange : every draft reviewed and at least one Rejected.
//   green  : drafts all Approved, OR job flagged "Invoice Not Needed".
//   red    : no invoices at all and not flagged "Invoice Not Needed".
function jobColorKey(job) {
  const drafts = job.unposted || [];
  const dec = job.decisions || {};
  if (drafts.some((d) => !dec[d.docNo])) return 'none';
  if (drafts.some((d) => dec[d.docNo] === 'Rejected')) return 'orange';
  if (drafts.length > 0) return 'green';
  if (job.jobFlag === 'Invoice Not Needed') return 'green';
  const hasPosted = /Posted Invoice/i.test(job.invoiceStatus || '') || parseInvoiceDocs(job.invoiceDocs).some((d) => d.isPosted);
  if (hasPosted) return 'none';
  return 'red';
}
// Statuses a job carries for the list filters (a job with several drafts can
// carry several). Each draft counts by its most recent decision, or Unreviewed
// (an unreviewed draft also marks the job 'UnreviewedInvoices'). With no draft,
// the job-level flag counts: Invoice Not Needed -> Approved, Need Invoice ->
// Rejected, none -> Unreviewed.
function jobStatuses(job) {
  const drafts = job.unposted || [];
  const dec = job.decisions || {};
  const out = new Set();
  if (drafts.length) {
    drafts.forEach((d) => {
      const s = dec[d.docNo];
      if (s === 'Approved' || s === 'Rejected') out.add(s);
      else { out.add('Unreviewed'); out.add('UnreviewedInvoices'); }
    });
  } else if (job.jobFlag === 'Invoice Not Needed') out.add('Approved');
  else if (job.jobFlag === 'Need Invoice') out.add('Rejected');
  else out.add('Unreviewed');
  return out;
}
const FILTERS = [
  { key: 'Rejected', label: 'Rejected' },
  { key: 'Approved', label: 'Approved' },
  { key: 'Unreviewed', label: 'All Unreviewed' },              // unreviewed drafts, or no draft and no job status
  { key: 'UnreviewedInvoices', label: 'Unreviewed Invoices' }, // only jobs with an unreviewed draft invoice
  { key: 'All', label: 'All' },
];

// Column sorts (key: jobNo | name | pm | director; dir: 1 asc, -1 desc).
// Project Manager / Director then sort by job name (ascending) within each.
const cmpText = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, { numeric: true, sensitivity: 'base' });
function sortJobs(jobs, { key, dir }) {
  const byName = (a, b) => cmpText(a.jobDescription, b.jobDescription) || cmpText(a.jobNo, b.jobNo);
  const cmp = {
    name: (a, b) => dir * byName(a, b),
    jobNo: (a, b) => dir * cmpText(a.jobNo, b.jobNo),
    director: (a, b) => dir * cmpText(a.director, b.director) || byName(a, b),
    pm: (a, b) => dir * cmpText(a.projectManager, b.projectManager) || byName(a, b),
  }[key] || byName;
  return [...jobs].sort(cmp);
}


const COLOR_BG = { green: '#ecfdf5', red: '#fef2f2', orange: '#fff7ed', none: 'transparent' };
const COLOR_BORDER = { green: '#6ee7b7', red: '#fca5a5', orange: '#fdba74', none: 'var(--gray-200)' };

// Open a posted invoice's DocImage PDF (A/R link) or an unposted synthetic PDF.
async function openPosted(invoiceNo) {
  try {
    const r = await api.getInvoiceDocImage(invoiceNo);
    if (r && r.link) window.open(r.link, '_blank', 'noopener');
    else alert(`No posted invoice document is on file for invoice ${invoiceNo}.`);
  } catch (e) {
    alert((e && ((e.responseBody && e.responseBody.error) || e.message)) || 'Failed to open the invoice document.');
  }
}
const openDraft = (docNo, jobNo) => window.open(api.getSyntheticInvoicePdfUrl(docNo, jobNo), '_blank', 'noopener');

// Posted invoices for a job (already sorted newest-first by the server): invoice
// (link to the real PDF), posting date, invoice amount, and unpaid amount. Shared
// by the list's "Show posted" expander and the entry screen. Shows 10 per page
// with Previous / Next, or the whole list via "Show all".
const POSTED_PAGE_SIZE = 10;
function PostedInvoicesTable({ posted, jobNo }) {
  const [page, setPage] = useState(0);
  const [showAll, setShowAll] = useState(false);
  if (!posted || posted.length === 0) {
    return <span style={{ color: 'var(--gray-400)', fontSize: 12 }}>No posted invoices.</span>;
  }
  const pageCount = Math.ceil(posted.length / POSTED_PAGE_SIZE);
  const cur = Math.min(page, pageCount - 1);
  const rows = showAll ? posted : posted.slice(cur * POSTED_PAGE_SIZE, (cur + 1) * POSTED_PAGE_SIZE);
  const pagerBtn = { fontSize: 12, padding: '2px 8px' };
  return (
    <div>
    <table style={{ borderCollapse: 'collapse', fontSize: 13, minWidth: 420 }}>
      <thead>
        <tr>
          <th style={th}>Invoice</th>
          <th style={th}>Date</th>
          <th style={thR}>Invoice Amount</th>
          <th style={thR}>Unpaid</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((p) => (
          <tr key={p.invoiceNo} style={{ borderTop: '1px solid var(--gray-100)' }}>
            <td style={td}>
              <button type="button" style={linkBtn} title="Open posted invoice PDF" onClick={() => openPosted(p.invoiceNo)}>{invNo(p.invoiceNo)}</button>
              <InvoiceExtras invoiceNo={p.invoiceNo} jobNo={jobNo} posted />
            </td>
            <td style={td}>{displayDate(p.postingDate)}</td>
            <td style={tdR}>{money(p.invoiceAmount)}</td>
            <td style={{ ...tdR, color: Number(p.outstandingBalance) > 0.005 ? '#b91c1c' : 'var(--gray-500)' }}>{money(p.outstandingBalance)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    {posted.length > POSTED_PAGE_SIZE && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: 12, color: 'var(--gray-600)' }}>
        {!showAll && (
          <>
            <IconButton icon="prev" title="Previous page" disabled={cur === 0} onClick={() => setPage(cur - 1)} />
            <span>Page {cur + 1} of {pageCount} ({posted.length} invoices)</span>
            <IconButton icon="next" title="Next page" disabled={cur >= pageCount - 1} onClick={() => setPage(cur + 1)} />
          </>
        )}
        <button type="button" className="btn btn-outline btn-sm" style={pagerBtn} onClick={() => setShowAll((v) => !v)}>
          {showAll ? `Show ${POSTED_PAGE_SIZE} per page` : 'Show all'}
        </button>
      </div>
    )}
    </div>
  );
}

// The job page's Invoice Approval tab: one job's approval screen, without the
// list or Previous/Next (the job page header shows the job, PM and Director).
export function InvoiceApprovalJob({ jobNo }) {
  const [jplModal, setJplModal] = useState(null);
  return (
    <ApprovalEntry key={jobNo} jobNo={jobNo} job={{}} single
      onViewEntries={(j, taskNo) => setJplModal({ job: j, task: taskNo || '' })}
      jplModal={jplModal} setJplModal={setJplModal} />
  );
}

export default function InvoiceApprovalView() {
  const { debugMode } = useDebug();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('list');   // 'list' | 'entry'
  const [currentJobNo, setCurrentJobNo] = useState('');
  const [expanded, setExpanded] = useState({}); // jobNo -> posted[] (lazy)
  const [jplModal, setJplModal] = useState(null);
  const [filter, setFilter] = useState('All');  // FILTERS key
  const [sort, setSort] = useState({ key: 'jobNo', dir: 1 }); // column sort
  const [canClear, setCanClear] = useState(false); // Clear Job Status not yet run this month
  const [clearing, setClearing] = useState(false);
  const [listBanner, setListBanner] = useState(null); // { kind, text }

  const loadJobs = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.getInvoiceApprovalJobs(debugMode);
      setJobs(Array.isArray(r.jobs) ? r.jobs : []);
      setCanClear(!!r.canClearJobStatus);
    } catch (e) {
      setError((e && e.message) || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [debugMode]);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  const toggleExpand = async (jobNo) => {
    if (expanded[jobNo]) { setExpanded((s) => ({ ...s, [jobNo]: undefined })); return; }
    try {
      const r = await api.getInvoiceApprovalJob(jobNo, debugMode);
      setExpanded((s) => ({ ...s, [jobNo]: (r && r.posted) || [] }));
    } catch (e) {
      setExpanded((s) => ({ ...s, [jobNo]: [] }));
    }
  };

  // Clear Job Status: a 'Clear' entry for every job with a Need Invoice /
  // Invoice Not Needed flag, so the new month starts without last month's flags.
  const clearJobStatus = async () => {
    if (!window.confirm('Clear the Need Invoice / Invoice Not Needed status on all jobs?')) return;
    setClearing(true); setListBanner(null);
    try {
      const r = await api.clearInvoiceApprovalJobStatus(debugMode);
      const failed = (r && r.failed) || [];
      setListBanner(failed.length
        ? { kind: 'err', text: `Cleared ${r.cleared} job(s); ${failed.length} failed: ${failed.map((f) => `${f.jobNo} (${f.error})`).join('; ')}` }
        : { kind: 'ok', text: `Cleared job status on ${r.cleared} job(s).` });
      await loadJobs();
    } catch (e) {
      setListBanner({ kind: 'err', text: (e && e.message) || 'Failed to clear job status' });
    } finally {
      setClearing(false);
    }
  };

  const statusByJob = new Map(jobs.map((j) => [j.jobNo, jobStatuses(j)]));
  const filterCount = (key) => (key === 'All' ? jobs.length : jobs.filter((j) => statusByJob.get(j.jobNo).has(key)).length);
  const shownJobs = sortJobs(filter === 'All' ? jobs : jobs.filter((j) => statusByJob.get(j.jobNo).has(filter)), sort);

  if (loading) return <div style={{ padding: 24 }}>Loading jobs…</div>;
  if (error) {
    return (
      <div style={{ maxWidth: 640, margin: '24px auto', padding: '0 16px' }}>
        <Banner kind="err">{error}</Banner>
        <button className="btn btn-outline btn-sm" style={{ marginTop: 8 }} onClick={loadJobs}>Retry</button>
      </div>
    );
  }

  if (view === 'entry') {
    // Previous / Next walk the (filtered) list's job order without returning to it.
    const idx = shownJobs.findIndex((x) => x.jobNo === currentJobNo);
    const prevJob = idx > 0 ? shownJobs[idx - 1].jobNo : '';
    const nextJob = idx >= 0 && idx < shownJobs.length - 1 ? shownJobs[idx + 1].jobNo : '';
    return (
      <ApprovalEntry
        key={currentJobNo}
        jobNo={currentJobNo}
        job={jobs.find((x) => x.jobNo === currentJobNo) || {}}
        position={idx >= 0 ? `${idx + 1} of ${shownJobs.length}` : ''}
        onPrev={prevJob ? () => setCurrentJobNo(prevJob) : null}
        onNext={nextJob ? () => setCurrentJobNo(nextJob) : null}
        onBack={() => { setView('list'); loadJobs(); }}
        onViewEntries={(jobNo, taskNo) => setJplModal({ job: jobNo, task: taskNo || '' })}
        jplModal={jplModal}
        setJplModal={setJplModal}
      />
    );
  }

  return (
    <div style={{ padding: '8px 16px 32px' }}>
      <PageHeader
        title="Invoice Approval"
        onRefresh={loadJobs}
        actions={canClear && (
          <button className="btn btn-outline btn-sm" onClick={clearJobStatus} disabled={clearing}>
            {clearing ? 'Clearing…' : 'Clear Job Status'}
          </button>
        )}
      />
      {listBanner && <Banner kind={listBanner.kind} onClose={() => setListBanner(null)}>{listBanner.text}</Banner>}
      <FilterButtons value={filter} onChange={setFilter}
        options={FILTERS.map((f) => ({ ...f, count: filterCount(f.key) }))} />
      {!jobs.length && <div style={{ padding: 16 }}>No open jobs found.</div>}
      {!!jobs.length && !shownJobs.length && <div style={{ padding: 16 }}>No jobs match this filter.</div>}
      {!!shownJobs.length && (
        <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
          <thead>
            <tr>
              <th style={th}>
                Job
                <SortArrows sort={sort} sortKey="jobNo" onSort={setSort} label="Number" />
                <SortArrows sort={sort} sortKey="name" onSort={setSort} label="Name" />
              </th>
              <th style={th}>Project Manager<SortArrows sort={sort} sortKey="pm" onSort={setSort} /></th>
              <th style={th}>Director<SortArrows sort={sort} sortKey="director" onSort={setSort} /></th>
              <th style={th}>Unposted invoices</th>
              <th style={th}>Posted</th>
            </tr>
          </thead>
          <tbody>
            {shownJobs.map((j) => {
              const ck = jobColorKey(j);
              const open = expanded[j.jobNo];
              return (
                <React.Fragment key={j.jobNo}>
                  <tr style={{ background: COLOR_BG[ck], borderTop: `1px solid ${COLOR_BORDER[ck]}` }}>
                    <td style={td}>
                      <button type="button" style={{ ...linkBtn, fontWeight: 600, whiteSpace: 'nowrap' }}
                        onClick={() => { setCurrentJobNo(j.jobNo); setView('entry'); }}
                        title="Open the approval screen for this job">
                        {j.jobNo}
                      </button>
                      <JobPageLink jobNo={j.jobNo} />
                      {j.jobDescription ? ` — ${j.jobDescription}` : ''}
                    </td>
                    <td style={td}>{j.projectManager}</td>
                    <td style={td}>{j.director}</td>
                    <td style={td}>
                      {(j.unposted || []).length === 0 ? (
                        // No draft: show the job-level flag if one was recorded, else "none".
                        j.jobFlag
                          ? <span style={approvalPill(j.jobFlag)}>{j.jobFlag}</span>
                          : <span style={{ color: 'var(--gray-400)' }}>none</span>
                      ) : (
                        (j.unposted || []).map((d, i) => {
                          const dec = (j.decisions || {})[d.docNo];
                          return (
                            <span key={d.docNo}>
                              {i > 0 && ', '}
                              <button type="button" style={linkBtn} title="View draft (unposted) invoice PDF" onClick={() => openDraft(d.docNo, j.jobNo)}>{invNo(d.docNo)}</button>
                              <InvoiceExtras invoiceNo={d.docNo} jobNo={j.jobNo} />
                              {dec && <span style={{ ...approvalPill(dec), marginLeft: 4 }}>{dec}</span>}
                            </span>
                          );
                        })
                      )}
                    </td>
                    <td style={td}>
                      {j.hasPosted ? (
                        <button type="button" style={linkBtn} onClick={() => toggleExpand(j.jobNo)}>
                          {open ? 'Hide posted' : 'Show posted'}
                        </button>
                      ) : <span style={{ color: 'var(--gray-400)' }}>none</span>}
                    </td>
                  </tr>
                  {open && (
                    <tr>
                      <td colSpan={5} style={{ ...td, background: 'var(--gray-50, #f9fafb)' }}>
                        <PostedInvoicesTable posted={open} jobNo={j.jobNo} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
      {jplModal && <JplModal job={jplModal.job} task={jplModal.task} onClose={() => setJplModal(null)} />}
    </div>
  );
}

// ---- Approval entry screen for one job ----
// Fixed rejection reasons; "Fix rates…" is separate (it picks an employee on the
// invoice and inserts "Fix rate of <name> to $").
const REJECT_TEMPLATES = ['Add better comments', 'Fix comment typos', 'Change lump sum billing to '];

function ApprovalEntry({ jobNo, job, position, onPrev, onNext, onBack, onViewEntries, jplModal, setJplModal, single }) {
  const { debugMode } = useDebug();
  const [tasks, setTasks] = useState([]);
  const [timeDetail, setTimeDetail] = useState({});
  const [detail, setDetail] = useState({ posted: [], pending: [], log: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // taskNo -> true when the user opened that task's time detail. All detail starts
  // collapsed; the component remounts per job (key=jobNo), so it re-collapses on
  // Previous / Next job and when the screen is left.
  const [openDetail, setOpenDetail] = useState({});
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(null);       // { kind, text }
  const [reject, setReject] = useState({});         // docNo -> reason text ('' closed/open via activeReject)
  const [activeReject, setActiveReject] = useState('');  // docNo currently rejecting
  const [flagComment, setFlagComment] = useState('');    // comment for Need Invoice / Invoice Not Needed
  const [showAllTasks, setShowAllTasks] = useState(false); // include closed task lines
  const [ratePicker, setRatePicker] = useState('');      // docNo whose "Fix rates" employee list is open
  const [previewRev, setPreviewRev] = useState(0);       // bumped by Refresh so the invoice preview re-renders

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [tResp, sResp, dResp] = await Promise.all([
        // Always fetch closed lines too; the "Show all task lines" toggle filters.
        api.getCreateInvoiceTasks(jobNo, debugMode, true),
        api.getCreateInvoiceTimeSummary(jobNo, debugMode).catch(() => ({ detail: [] })),
        api.getInvoiceApprovalJob(jobNo, debugMode),
      ]);
      setTasks(Array.isArray(tResp.tasks) ? tResp.tasks : []);
      const dmap = {};
      (Array.isArray(sResp.detail) ? sResp.detail : []).forEach((r) => {
        const k = String(r.JobTaskNo);
        if (!dmap[k]) dmap[k] = [];
        dmap[k].push(r);
      });
      setTimeDetail(dmap);
      setDetail({ posted: dResp.posted || [], pending: dResp.pending || [], log: dResp.log || [] });
    } catch (e) {
      setError((e && e.message) || 'Failed to load the job');
    } finally {
      setLoading(false);
    }
  }, [jobNo, debugMode]);
  useEffect(() => { load(); }, [load]);
  // Refresh: reload the job's data and re-generate the draft invoice preview.
  const refresh = () => { setPreviewRev((n) => n + 1); load(); };

  // Append a reason fragment to a draft's rejection comment ("; "-separated).
  const appendReason = (docNo, text) =>
    setReject((s) => ({ ...s, [docNo]: (s[docNo] ? s[docNo] + '; ' : '') + text }));

  // Task lines shown: open only, or all (incl. closed) when toggled.
  const closedCount = tasks.filter((t) => Number(t.TaskClosed) === 1).length;
  const visibleTasks = showAllTasks ? tasks : tasks.filter((t) => Number(t.TaskClosed) !== 1);

  // Job-level entries (no invoice), newest first.
  const jobLog = detail.log.filter((e) => !e.invoiceNo);

  // Each draft's decisions, newest first (the log is already newest-first).
  const historyByInvoice = useMemo(() => {
    const m = {};
    for (const e of detail.log) {
      if (!e.invoiceNo) continue;
      (m[e.invoiceNo] = m[e.invoiceNo] || []).push(e);
    }
    return m;
  }, [detail.log]);

  const submit = async (payload, okText) => {
    setBusy(true); setBanner(null);
    try {
      await api.postInvoiceApprovalDecision(jobNo, payload, debugMode);
      setBanner({ kind: 'ok', text: okText });
      setActiveReject(''); setFlagComment('');
      await load();
    } catch (e) {
      setBanner({ kind: 'err', text: (e && e.message) || 'Failed to save the decision' });
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading job…</div>;

  const pending = detail.pending || [];
  const onePending = pending.length === 1;

  return (
    // Normal column width, except the task lines (WideSection: up to the full
    // screen width, only as wide as needed).
    <div style={{ padding: '8px 16px 32px' }}>
      <PageHeader
        center
        title={single ? null : <>{jobNo}{job.jobDescription ? ` — ${job.jobDescription}` : ''}<JobPageLink jobNo={jobNo} /></>}
        onBack={single ? undefined : onBack}
        onPrev={single ? undefined : onPrev}
        onNext={single ? undefined : onNext}
        prevTitle="Previous job"
        nextTitle="Next job"
        position={position}
        onRefresh={refresh}
      />
      <Banner kind="err">{error}</Banner>
      {banner && <Banner kind={banner.kind} onClose={() => setBanner(null)}>{banner.text}</Banner>}

      {/* Job-level approval history only (Need Invoice / Invoice Not Needed since
          the last Clear), newest first. Per-invoice entries show under their invoice. */}
      {jobLog.length > 0 && (
        <div style={{ margin: '8px 0 12px' }}>
          <h3 style={{ margin: '8px 0' }}>Approval history</h3>
          <ApprovalHistoryTable entries={jobLog} headers />
        </div>
      )}

      {/* Task lines: only as wide as their content needs (up to the full screen
          width), centered. Long time-detail notes wrap rather than widen it. */}
      <WideSection>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', fontSize: 13, color: 'var(--gray-600)' }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={showAllTasks} onChange={(e) => setShowAllTasks(e.target.checked)} />
          Show all task lines (including closed{closedCount ? `: ${closedCount}` : ''})
        </label>
        {!single && <span>Project Manager: <strong style={{ color: 'var(--gray-800)' }}>{job.projectManager || '—'}</strong></span>}
        {!single && <span>Director: <strong style={{ color: 'var(--gray-800)' }}>{job.director || '—'}</strong></span>}
      </div>

      {/* Read-only task lines (shared with Create Invoices) */}
      {visibleTasks.length === 0 ? (
        <div style={{ padding: 16 }}>{showAllTasks ? 'No posting task lines on this job.' : 'No open posting task lines on this job.'}</div>
      ) : (
        <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
          <thead><tr><TaskDataHeaders showTotalBudget /></tr></thead>
          <tbody>
            {visibleTasks.map((t) => {
              const detailRows = timeDetail[String(t.JobTaskNo)] || [];
              const detailOpen = detailRows.length > 0 && !!openDetail[t.JobTaskNo];
              const closed = Number(t.TaskClosed) === 1;
              return (
                <React.Fragment key={t.JobTaskNo}>
                  <tr style={{ borderTop: '1px solid var(--gray-200)', color: closed ? 'var(--gray-500)' : undefined }}>
                    <TaskDataCells t={t} jobNo={jobNo} onViewEntries={onViewEntries} showTotalBudget entriesInType />
                  </tr>
                  {detailRows.length > 0 && (
                    <tr style={{ background: 'var(--gray-50, #f9fafb)' }}>
                      <td colSpan={taskDataColumnCount({ showTotalBudget: true })} style={{ padding: '0 8px 8px 8px', borderTop: 'none' }}>
                        {/* width 0 / min-width 100%: fills the row without adding to the table's width */}
                        <div style={{ width: 0, minWidth: '100%' }}>
                          <TimeDetailBlock rows={detailRows} open={detailOpen}
                            onToggle={() => setOpenDetail((c) => ({ ...c, [t.JobTaskNo]: !c[t.JobTaskNo] }))} />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
      </WideSection>

      {/* Approval actions (above the single-draft preview) */}
      <div style={{ marginTop: 16 }}>
        <h3 style={{ margin: '8px 0' }}>Approval</h3>
        {pending.length > 0 ? (
          pending.map((p) => {
            const history = historyByInvoice[p.docNo] || [];
            return (
              <div key={p.docNo} style={{ border: '1px solid var(--gray-200)', borderRadius: 8, padding: 12, marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <strong>
                    <button type="button" style={linkBtn} onClick={() => openDraft(p.docNo, jobNo)}>{invNo(p.docNo)}</button>
                  </strong>
                  <InvoiceExtras invoiceNo={p.docNo} jobNo={jobNo} />
                  <span style={{ color: 'var(--gray-600)', fontSize: 13 }}>{money(p.amount)}</span>
                  <span style={{ flex: 1 }} />
                  <button className="btn btn-primary btn-sm" disabled={busy}
                    onClick={() => submit({ invoiceNo: p.docNo, decision: 'Approved', reason: '' }, `Invoice ${p.docNo} approved.`)}>
                    Approved
                  </button>
                  <button className="btn btn-outline btn-sm" disabled={busy}
                    onClick={() => setActiveReject(activeReject === p.docNo ? '' : p.docNo)}>
                    Fix / Reject
                  </button>
                </div>
                {/* T&M time on this invoice billed below a person's default rate code.
                    "Reject for this rate issue" adds a shorter, dollars-only version to the
                    rejection reason. */}
                {(p.rateWarnings || []).map((w) => (
                  <Banner key={w.text} kind="warn" style={{ margin: '8px 0 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ flex: 1, minWidth: 200 }}>{w.text}</span>
                      <button type="button" className="btn btn-outline btn-sm" style={{ fontSize: 11 }} disabled={busy}
                        onClick={() => { appendReason(p.docNo, w.reason); setActiveReject(p.docNo); }}>
                        Reject for this rate issue
                      </button>
                    </div>
                  </Banner>
                ))}
                {/* This invoice's full status history, newest first. */}
                {history.length > 0 && (
                  <div style={{ marginTop: 8 }}><ApprovalHistoryTable entries={history} /></div>
                )}
                {activeReject === p.docNo && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                      {REJECT_TEMPLATES.map((tpl) => (
                        <button key={tpl} type="button" className="btn btn-outline btn-sm" style={{ fontSize: 11 }}
                          onClick={() => appendReason(p.docNo, tpl)}>
                          {tpl}
                        </button>
                      ))}
                      <button type="button" className="btn btn-outline btn-sm" style={{ fontSize: 11 }}
                        onClick={() => setRatePicker(ratePicker === p.docNo ? '' : p.docNo)}>
                        Fix rates…
                      </button>
                    </div>
                    {/* Employees ON this invoice; picking one inserts "Fix rate of <name> to $". */}
                    {ratePicker === p.docNo && (
                      <div style={{ margin: '0 0 6px', padding: '6px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, background: 'var(--gray-50, #f9fafb)' }}>
                        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 4 }}>Choose the employee whose rate needs fixing:</div>
                        {(p.employees || []).length === 0 ? (
                          <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>No employees are on this invoice.</span>
                        ) : (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {p.employees.map((name) => (
                              <button key={name} type="button" className="btn btn-outline btn-sm" style={{ fontSize: 11 }}
                                onClick={() => { appendReason(p.docNo, `Fix rate of ${name} to $`); setRatePicker(''); }}>
                                {name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <textarea className="form-control" rows={2} value={reject[p.docNo] || ''}
                      placeholder="Reason for the fix (required)…"
                      onChange={(e) => setReject((s) => ({ ...s, [p.docNo]: e.target.value }))} maxLength={250} />
                    <div style={{ marginTop: 6 }}>
                      <button className="btn btn-primary btn-sm" disabled={busy || !(reject[p.docNo] || '').trim()}
                        onClick={() => submit({ invoiceNo: p.docNo, decision: 'Rejected', reason: reject[p.docNo] || '' }, `Invoice ${p.docNo} rejected.`)}>
                        Submit rejection
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div style={{ border: '1px solid var(--gray-200)', borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 13, color: 'var(--gray-600)', marginBottom: 8 }}>
              This job has no unposted invoice.
            </div>
            <textarea className="form-control" rows={2} value={flagComment}
              placeholder="Optional comment…" onChange={(e) => setFlagComment(e.target.value)} maxLength={250} />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="btn btn-primary btn-sm" disabled={busy}
                onClick={() => submit({ invoiceNo: '', decision: 'Need Invoice', reason: flagComment }, 'Flagged: Need Invoice.')}>
                Need Invoice
              </button>
              <button className="btn btn-outline btn-sm" disabled={busy}
                onClick={() => submit({ invoiceNo: '', decision: 'Invoice Not Needed', reason: flagComment }, 'Flagged: Invoice Not Needed.')}>
                Invoice Not Needed
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Single draft preview */}
      {onePending && (
        <div style={{ marginTop: 16 }}>
          <iframe key={previewRev} title="Draft invoice preview"
            src={`${api.getSyntheticInvoicePdfUrl(pending[0].docNo, jobNo)}${previewRev ? `${jobNo ? '&' : '?'}r=${previewRev}` : ''}`}
            style={{ width: '100%', height: 640, border: '1px solid var(--gray-300)', borderRadius: 8 }} />
        </div>
      )}

      {/* Posted invoices (view-only) */}
      {detail.posted.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <h3 style={{ margin: '8px 0' }}>Posted invoices</h3>
          <PostedInvoicesTable posted={detail.posted} jobNo={jobNo} />
        </div>
      )}

      {jplModal && <JplModal job={jplModal.job} task={jplModal.task} onClose={() => setJplModal(null)} />}
    </div>
  );
}
